import React from 'react';
import {Tag, Tooltip} from 'antd';
import {WarningOutlined, CheckCircleOutlined, CloseCircleOutlined, SyncOutlined} from '@ant-design/icons';
import {FormattedMessage, useIntl} from 'react-intl';

export type RowStatus =
    | 'draft'
    | 'valid'
    | 'invalid'
    | 'duplicate_warning'
    | 'conflict'
    | 'saving'
    | 'saved'
    | 'failed'
    | 'skipped';

interface DupInfo {
    type: string;
    name?: string;
    patientId?: string;
}

interface Props {
    status: RowStatus;
    errors?: Record<string, string[]>;
    duplicateInfo?: DupInfo[];
}

const STATUS_COLOR: Record<RowStatus, string> = {
    draft: 'default',
    valid: 'success',
    invalid: 'error',
    duplicate_warning: 'warning',
    conflict: 'orange',
    saving: 'processing',
    saved: 'success',
    failed: 'error',
    skipped: 'default',
};

const STATUS_ICON: Partial<Record<RowStatus, React.ReactNode>> = {
    valid: <CheckCircleOutlined/>,
    saved: <CheckCircleOutlined/>,
    invalid: <CloseCircleOutlined/>,
    failed: <CloseCircleOutlined/>,
    duplicate_warning: <WarningOutlined/>,
    conflict: <WarningOutlined/>,
    saving: <SyncOutlined spin/>,
};

// Maps backend camelCase field keys → i18n message IDs for display labels.
// Keys match exactly what the server puts in validationErrors.
const FIELD_LABEL_ID: Record<string, string> = {
    firstName:  'first_name',
    lastName:   'last_name',
    secondName: 'surname',
    email:      'email',
    phone:      'phone',
    birthDate:  'birth_date',
    gender:     'gender',
    addressOne: 'addressOne',
    nationalId: 'egn',
};

/**
 * Fixed-height status indicator for the import grid.
 *
 * Label shows a concise single-line summary — no inlineDetail divs that would
 * break row height stability.  Full per-field details are in the Tooltip.
 */
export const RowStatusTag: React.FC<Props> = ({status, errors, duplicateInfo}) => {
    const intl = useIntl();

    // Resolve a backend field key to a translated display label.
    const fieldLabel = (field: string): string => {
        const id = FIELD_LABEL_ID[field];
        return id ? intl.formatMessage({id}) : field;
    };

    // --- Concise single-line tag label ---
    let tagLabel: React.ReactNode;
    if (status === 'invalid' && errors) {
        const fields = Object.keys(errors);
        if (fields.length === 1) {
            // Show "<Label>: <first error message>" — e.g. "First name: Letters only"
            tagLabel = `${fieldLabel(fields[0])}: ${Object.values(errors)[0][0]}`;
        } else {
            tagLabel = intl.formatMessage({id: 'import_fix_n_issues'}, {count: fields.length});
        }
    } else {
        tagLabel = <FormattedMessage id={`import_row_status_${status}`}/>;
    }

    // --- Tooltip: full per-field details on hover ---
    const tooltipLines: string[] = [];
    if (errors) {
        Object.entries(errors).forEach(([field, msgs]) => {
            const label = fieldLabel(field);
            msgs.forEach(msg => tooltipLines.push(`${label}: ${msg}`));
        });
    }
    if (duplicateInfo) {
        duplicateInfo.forEach(d => {
            tooltipLines.push(d.name ? `${d.type}: ${d.name}` : d.type);
        });
    }

    const hasNatIdDup = duplicateInfo?.some(d => d.type === 'national_id') ?? false;
    const tagColor = status === 'duplicate_warning' && hasNatIdDup
        ? 'orange'
        : (STATUS_COLOR[status] ?? 'default');
    const icon = STATUS_ICON[status];

    const tag = (
        <Tag
            color={tagColor}
            icon={icon}
            style={{cursor: tooltipLines.length ? 'help' : 'default', fontSize: 11, maxWidth: 106, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
        >
            {tagLabel}
        </Tag>
    );

    return tooltipLines.length
        ? <Tooltip title={tooltipLines.map((l, i) => <div key={i}>{l}</div>)}>{tag}</Tooltip>
        : tag;
};
